import React, { useState } from 'react';
import {
  faEnvelope,
  faCheckCircle,
  faPaperPlane
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/Button';
import Alert from '../components/Alert';
import { AuthService } from '../services/authService';

const EmailVerificationPage: React.FC = () => {
  const { user, loading, isEmailVerified } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  const resendVerificationEmail = async (): Promise<void> => {
    if (!user?.email) {
      setError('Email do usuário não encontrado');
      return;
    }

    setIsLoading(true);
    setError(undefined);
    setMessage(undefined);

    try {
      const { error } = await AuthService.resendVerificationEmail(user.email);

      if (error) {
        throw new Error(error.message);
      }

      setMessage('Email de verificação reenviado com sucesso! Verifique sua caixa de entrada.');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro inesperado ao reenviar email';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // If user is not logged in, redirect to login
  if (!user && !loading) {
    return <Navigate to="/login" replace />;
  }

  // If email is already verified, redirect to appropriate page
  if (isEmailVerified()) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          <div className="relative">
            <div className="w-16 h-16 bg-white rounded-xl shadow-lg flex items-center justify-center border border-gray-100">
              <img
                className="w-10 h-10 object-contain"
                src="/logoipsum-custom-logogram.svg"
                alt="OptoSoft Logo"
              />
            </div>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          OptoSoft
        </h1>
        <p className="text-gray-600 text-sm">
          Sistema de Gestão Clínica
        </p>
      </div>

      {/* Email Verification Form */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon icon={faEnvelope} className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifique seu Email</h2>
          <p className="text-gray-600 text-sm">
            Para continuar usando o sistema, você precisa verificar seu endereço de email
          </p>
        </div>

        <div className="space-y-6">
          {/* User Email Display */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faEnvelope} className="w-5 h-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                <p className="text-xs text-gray-500">Email aguardando verificação</p>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">
              <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 mr-2" />
              O que fazer:
            </h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>1. Verifique sua caixa de entrada de email</li>
              <li>2. Procure por um email de verificação do OptoSoft</li>
              <li>3. Clique no link de confirmação no email</li>
              <li>4. Volte a esta página - você será redirecionado automaticamente</li>
            </ul>
          </div>

          {/* Success Message */}
          {message && (
            <Alert
              variant="success"
              title="Email Enviado"
              message={message}
            />
          )}

          {/* Error Message */}
          {error && (
            <Alert
              variant="error"
              title="Erro ao Enviar Email"
              message={error}
            />
          )}

          {/* Resend Button */}
          <Button
            onClick={resendVerificationEmail}
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            leftIcon={!isLoading ? faPaperPlane : undefined}
          >
            {isLoading ? 'Enviando...' : 'Reenviar Email de Verificação'}
          </Button>

          {/* Help Text */}
          <div className="text-center">
            <p className="text-xs text-gray-500">
              Não recebeu o email? Verifique sua pasta de spam ou clique em "Reenviar"
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Desenvolvido por{' '}
            <a
              href="https://um0um.com.br"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              101 Soluções
            </a>
          </p>
        </div>
      </div>
    </>
  );
};

export default EmailVerificationPage;